import React, { useState } from 'react';
import { Container, Table, Button, Card, Row as BootstrapRow, Col as BootstrapCol } from 'react-bootstrap';
import { Select, Form } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from '@/common/PageTitle/PageTitle';
import styles from './client-roles.module.scss';

interface ClientRole {
  id: string;
  roleName: string;
  description: string;
  permissions: string;
  users: number;
}

const ClientRoles: React.FC = () => {
  // Filter state
  const [filters, setFilters] = useState({
    roleName: undefined as string | undefined,
    permissions: undefined as string | undefined,
    users: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Client Roles
  const clientRoles: ClientRole[] = [
    {
      id: 'admin',
      roleName: 'Admin',
      description: 'Full administrative access to all client features',
      permissions: 'All',
      users: 0
    },
    {
      id: 'manager',
      roleName: 'Manager',
      description: 'Can manage client accounts and oversee operations',
      permissions: 'Limited',
      users: 0
    },
    {
      id: 'marketing',
      roleName: 'Marketing',
      description: 'Access to marketing and promotional features',
      permissions: 'Limited',
      users: 0
    },
    {
      id: 'user',
      roleName: 'User',
      description: 'Basic user access to client services',
      permissions: 'Basic',
      users: 0
    }
  ];

  // Filter options
  const filterOptions = {
    roleNames: clientRoles.map(role => role.roleName),
    permissions: [...new Set(clientRoles.map(role => role.permissions))],
    users: ['0', '1-5', '6-10', '10+'], // User count ranges
  };

  // Filtering logic
  const filteredRoles = clientRoles.filter(role => {
    return (
      (!filters.roleName || role.roleName === filters.roleName) &&
      (!filters.permissions || role.permissions === filters.permissions) &&
      (!filters.users || (
        filters.users === '0' && role.users === 0 ||
        filters.users === '1-5' && role.users >= 1 && role.users <= 5 ||
        filters.users === '6-10' && role.users >= 6 && role.users <= 10 ||
        filters.users === '10+' && role.users > 10
      ))
    );
  });

  const handleCreateRole = () => {
    console.log('Create client role');
  };

  const handleAction = (action: string, roleId: string) => {
    console.log(`${action} role:`, roleId);
  };

  // Filter change handlers
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ roleName: undefined, permissions: undefined, users: undefined });
    setResetCounter(prev => prev + 1);
  };

  const renderRoleTable = (roles: ClientRole[]) => (
    <Table hover responsive className={styles.clientRolesTable}>
      <thead>
        <tr>
          <th>Role Name</th>
          <th>Description</th>
          <th>Permissions</th>
          <th>Users</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {roles.map((role) => (
          <tr key={role.id}>
            <td>{role.roleName}</td>
            <td>{role.description}</td>
            <td>{role.permissions}</td>
            <td>{role.users}</td>
            <td>
              <span 
                className={styles.actionButton}
                onClick={() => handleAction('view', role.id)}
              >
                ⋮
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Client Roles" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <div className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Role Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`roleName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any role name"
                            value={filters.roleName}
                            onChange={value => handleFilterChange('roleName', value)}
                            options={filterOptions.roleNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Permissions"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`permissions-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any permissions"
                            value={filters.permissions}
                            onChange={value => handleFilterChange('permissions', value)}
                            options={filterOptions.permissions.map(permission => ({ label: permission, value: permission }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Users"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`users-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any user count"
                            value={filters.users}
                            onChange={value => handleFilterChange('users', value)}
                            options={filterOptions.users.map(user => ({ label: user, value: user }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol 
                        md={12}
                        className="d-flex justify-content-end"
                      >
                        <Button
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px'
                          }}
                          onClick={handleCreateRole}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add New Role
                        </Button>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </div>
          
          {renderRoleTable(filteredRoles)}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ClientRoles; 